import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, Cell } from "recharts";

import { API_BASE as API } from '../config';
const STRATEGY = "ATR_VOL_m20lb14v15_SL20";
const SYMBOLS = ["ETHUSDT", "BTCUSDT", "SOLUSDT"];

const REGIME_COLORS = {
  strong_bull: "#22c55e", mild_bull: "#86efac", ranging: "#fbbf24",
  mild_bear: "#fb923c", strong_bear: "#ef4444", high_vol: "#a855f7", unknown: "#6b7280",
};

function VerdictBadge({ light }) {
  const colors = { GREEN: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", YELLOW: "bg-amber-500/20 text-amber-400 border-amber-500/30", RED: "bg-red-500/20 text-red-400 border-red-500/30" };
  return (
    <span className={`px-3 py-1 rounded border font-mono text-sm ${colors[light] || colors.RED}`} data-testid="verdict-badge">
      {light}
    </span>
  );
}

function MetricCard({ label, value, suffix = "", good, warn }) {
  const numVal = parseFloat(value);
  let color = "text-zinc-300";
  if (good !== undefined && numVal >= good) color = "text-emerald-400";
  else if (warn !== undefined && numVal >= warn) color = "text-amber-400";
  else if (good !== undefined) color = "text-red-400";
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-mono font-semibold ${color}`}>{value}{suffix}</div>
    </div>
  );
}

function TestResultRow({ name, stats, baseline }) {
  if (!stats) return null;
  const degradation = baseline && baseline.total_return_pct > 0
    ? ((baseline.total_return_pct - stats.total_return_pct) / baseline.total_return_pct * 100).toFixed(1)
    : "N/A";
  const degNum = parseFloat(degradation);
  const degColor = degNum <= 15 ? "text-emerald-400" : degNum <= 30 ? "text-amber-400" : "text-red-400";
  return (
    <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`test-row-${name}`}>
      <td className="py-2 px-3 text-sm font-medium text-zinc-200">{name}</td>
      <td className="py-2 px-3 text-sm font-mono text-right">{stats.total_return_pct?.toFixed(1)}%</td>
      <td className="py-2 px-3 text-sm font-mono text-right">{stats.sharpe?.toFixed(2)}</td>
      <td className="py-2 px-3 text-sm font-mono text-right">{stats.max_drawdown_pct?.toFixed(1)}%</td>
      <td className="py-2 px-3 text-sm font-mono text-right">{stats.profit_factor?.toFixed(2)}</td>
      <td className="py-2 px-3 text-sm font-mono text-right">{stats.trades}</td>
      <td className="py-2 px-3 text-sm font-mono text-right">{stats.win_rate?.toFixed(1)}%</td>
      <td className={`py-2 px-3 text-sm font-mono text-right ${degColor}`}>
        {degradation !== "N/A" ? `${degradation}%` : "—"}
      </td>
    </tr>
  );
}

export default function ValidationPage() {
  const { token } = useAuth();
  const [results, setResults] = useState({});
  const [hybridResults, setHybridResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeSymbol, setActiveSymbol] = useState("ETHUSDT");
  const [runningTasks, setRunningTasks] = useState({});

  const fetchResults = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const allResults = {};
    const allHybrid = {};

    for (const sym of SYMBOLS) {
      try {
        const [valRes, hybRes] = await Promise.all([
          fetch(`${API}/api/validate-strategy/${STRATEGY}/results?symbol=${sym}`, { headers }).then(r => r.json()),
          fetch(`${API}/api/validate-hybrid/${STRATEGY}/results?symbol=${sym}`, { headers }).then(r => r.json()),
        ]);
        if (valRes && valRes.strategy_name) allResults[sym] = valRes;
        if (hybRes && hybRes.verdict) allHybrid[sym] = hybRes;
      } catch (e) { /* ignore */ }
    }
    setResults(allResults);
    setHybridResults(allHybrid);
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const launchValidation = async (symbol) => {
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    try {
      const [valRes, hybRes] = await Promise.all([
        fetch(`${API}/api/validate-strategy/${STRATEGY}?days=3150&symbol=${symbol}`, { method: "POST", headers }).then(r => r.json()),
        fetch(`${API}/api/validate-hybrid/${STRATEGY}?days=3150&symbol=${symbol}`, { method: "POST", headers }).then(r => r.json()),
      ]);
      setRunningTasks(prev => ({
        ...prev,
        [symbol]: { val: valRes.task_id, hyb: hybRes.task_id, progress: 0 },
      }));
      pollTask(symbol, valRes.task_id, hybRes.task_id);
    } catch (e) { console.error(e); }
  };

  const pollTask = async (symbol, valId, hybId) => {
    const headers = { Authorization: `Bearer ${token}` };
    const poll = setInterval(async () => {
      try {
        const [valStatus, hybStatus] = await Promise.all([
          fetch(`${API}/api/tasks/${valId}`, { headers }).then(r => r.json()),
          fetch(`${API}/api/tasks/${hybId}`, { headers }).then(r => r.json()),
        ]);
        const avgProgress = ((valStatus.progress || 0) + (hybStatus.progress || 0)) / 2;
        setRunningTasks(prev => ({ ...prev, [symbol]: { ...prev[symbol], progress: avgProgress } }));
        if (valStatus.status === "complete" && hybStatus.status === "complete") {
          clearInterval(poll);
          setRunningTasks(prev => { const n = { ...prev }; delete n[symbol]; return n; });
          fetchResults();
        }
        if (valStatus.status === "error" || hybStatus.status === "error") {
          clearInterval(poll);
          setRunningTasks(prev => { const n = { ...prev }; delete n[symbol]; return n; });
        }
      } catch (e) { clearInterval(poll); }
    }, 5000);
  };

  const symData = results[activeSymbol];
  const hybData = hybridResults[activeSymbol];

  const equityCurves = hybData?.equity_curves || {};
  const chartData = [];
  if (equityCurves.A_baseline) {
    const maxLen = Math.max(
      equityCurves.A_baseline?.length || 0, equityCurves.B_pure_taker?.length || 0,
      equityCurves.C_hybrid_raw?.length || 0, equityCurves.D_hybrid_filtered?.length || 0,
    );
    const step = Math.max(1, Math.floor(maxLen / 300));
    for (let i = 0; i < maxLen; i += step) {
      const point = { candle: equityCurves.A_baseline?.[i]?.candle || i };
      if (equityCurves.A_baseline?.[i]) point.baseline = equityCurves.A_baseline[i].pnl_pct;
      if (equityCurves.B_pure_taker?.[i]) point.taker = equityCurves.B_pure_taker[i].pnl_pct;
      if (equityCurves.C_hybrid_raw?.[i]) point.hybrid = equityCurves.C_hybrid_raw[i].pnl_pct;
      if (equityCurves.D_hybrid_filtered?.[i]) point.filtered = equityCurves.D_hybrid_filtered[i].pnl_pct;
      chartData.push(point);
    }
  }

  const regimeData = symData?.regime_sanity?.regime_breakdown || [];

  return (
    <div className="space-y-6 p-1" data-testid="validation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 font-mono tracking-tight" data-testid="page-title">
            VALIDATION SUITE
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {STRATEGY} — 5-Part Stress Test + Hybrid Execution Model
          </p>
        </div>
        <div className="flex gap-2">
          {SYMBOLS.map(sym => (
            <button
              key={sym}
              data-testid={`symbol-tab-${sym}`}
              onClick={() => setActiveSymbol(sym)}
              className={`px-4 py-2 rounded text-sm font-mono transition-all ${
                activeSymbol === sym
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                  : "bg-zinc-800/60 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
              }`}
            >
              {sym.replace("USDT", "")}
              {runningTasks[sym] && (
                <span className="ml-2 text-xs text-amber-400 animate-pulse">
                  {runningTasks[sym].progress.toFixed(0)}%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-zinc-500 font-mono animate-pulse" data-testid="loading-indicator">
          Loading validation results...
        </div>
      ) : !symData && !hybData ? (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-8 text-center" data-testid="no-results">
          <p className="text-zinc-400 mb-4">No validation results for {activeSymbol} yet.</p>
          <button
            data-testid="launch-validation-btn"
            onClick={() => launchValidation(activeSymbol)}
            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded font-mono text-sm transition-colors"
          >
            Launch Full Validation
          </button>
        </div>
      ) : (
        <>
          {/* Verdict Banner */}
          {hybData?.verdict && (
            <div className={`border rounded-lg p-4 ${
              hybData.verdict.light === "GREEN" ? "bg-emerald-950/30 border-emerald-800/50" :
              hybData.verdict.light === "YELLOW" ? "bg-amber-950/30 border-amber-800/50" :
              "bg-red-950/30 border-red-800/50"
            }`} data-testid="verdict-banner">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <VerdictBadge light={hybData.verdict.light} />
                  <span className="text-sm text-zinc-300 font-mono">{hybData.verdict.action}</span>
                </div>
                <div className="text-xs text-zinc-500">
                  Degradation from baseline: <span className="text-zinc-300">{hybData.verdict.degradation_from_baseline_pct}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Quick Metrics Row */}
          {hybData?.scenarios?.D_hybrid_filtered && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="metrics-grid">
              <MetricCard label="Return" value={hybData.scenarios.D_hybrid_filtered.total_return_pct?.toFixed(1)} suffix="%" />
              <MetricCard label="Sharpe" value={hybData.scenarios.D_hybrid_filtered.sharpe?.toFixed(2)} good={2.5} warn={2.0} />
              <MetricCard label="Max DD" value={hybData.scenarios.D_hybrid_filtered.max_drawdown_pct?.toFixed(1)} suffix="%" good={0} warn={-25} />
              <MetricCard label="Profit Factor" value={hybData.scenarios.D_hybrid_filtered.profit_factor?.toFixed(2)} good={1.8} warn={1.5} />
              <MetricCard label="Win Rate" value={hybData.scenarios.D_hybrid_filtered.win_rate?.toFixed(1)} suffix="%" />
              <MetricCard label="Trades" value={hybData.scenarios.D_hybrid_filtered.trades} />
              <MetricCard label="Max Consec L" value={hybData.scenarios.D_hybrid_filtered.max_consecutive_losses} />
            </div>
          )}

          {/* 5-Part Test Results Table */}
          {symData && (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden" data-testid="five-part-table">
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                <h2 className="text-base font-semibold text-zinc-200">5-Part Stress Test — {activeSymbol}</h2>
                <span className="text-xs text-zinc-500 font-mono">{symData.candles?.toLocaleString()} candles | {symData.days} days</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-800/40 text-zinc-400 text-xs uppercase">
                      <th className="py-2 px-3 text-left">Test</th>
                      <th className="py-2 px-3 text-right">Return%</th>
                      <th className="py-2 px-3 text-right">Sharpe</th>
                      <th className="py-2 px-3 text-right">MaxDD%</th>
                      <th className="py-2 px-3 text-right">PF</th>
                      <th className="py-2 px-3 text-right">Trades</th>
                      <th className="py-2 px-3 text-right">WR%</th>
                      <th className="py-2 px-3 text-right">Degradation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TestResultRow name="Baseline (no friction)" stats={symData.baseline} baseline={null} />
                    <TestResultRow name="1. Lookahead Killer" stats={symData.lookahead_killer} baseline={symData.baseline} />
                    <TestResultRow name="2. Slippage + Fees" stats={symData.slippage_fees} baseline={symData.baseline} />
                    <TestResultRow name="3. Timing Robustness" stats={symData.timing_robustness} baseline={symData.baseline} />
                    <TestResultRow name="4. Spread/Fill" stats={symData.spread_fill} baseline={symData.baseline} />
                    <TestResultRow name="5. Regime Sanity" stats={symData.regime_sanity} baseline={symData.baseline} />
                  </tbody>
                </table>
              </div>
              {symData.analysis && (
                <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-800/20">
                  <span className="text-xs text-zinc-500">Verdict: </span>
                  <span className="text-sm font-mono text-zinc-200">{symData.analysis.verdict}</span>
                </div>
              )}
            </div>
          )}

          {/* Hybrid Execution Comparison Table */}
          {hybData?.scenarios && (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden" data-testid="hybrid-table">
              <div className="px-4 py-3 border-b border-zinc-800">
                <h2 className="text-base font-semibold text-zinc-200">Hybrid Execution Model — {activeSymbol}</h2>
                <p className="text-xs text-zinc-500 mt-1">
                  Config: {hybData.config?.maker_fill_rate} maker | Fees: maker {hybData.config?.maker_fee_pct}% / taker {hybData.config?.taker_fee_pct}% | Min move: {hybData.config?.min_move_filter}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-800/40 text-zinc-400 text-xs uppercase">
                      <th className="py-2 px-3 text-left">Scenario</th>
                      <th className="py-2 px-3 text-right">Return%</th>
                      <th className="py-2 px-3 text-right">Sharpe</th>
                      <th className="py-2 px-3 text-right">MaxDD%</th>
                      <th className="py-2 px-3 text-right">PF</th>
                      <th className="py-2 px-3 text-right">Trades</th>
                      <th className="py-2 px-3 text-right">WR%</th>
                      <th className="py-2 px-3 text-right">Degradation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TestResultRow name="A. Baseline" stats={hybData.scenarios.A_baseline} baseline={null} />
                    <TestResultRow name="B. Pure Taker (worst)" stats={hybData.scenarios.B_pure_taker} baseline={hybData.scenarios.A_baseline} />
                    <TestResultRow name="C. Hybrid 70/30" stats={hybData.scenarios.C_hybrid_raw} baseline={hybData.scenarios.A_baseline} />
                    <TestResultRow name="D. Hybrid + Filters" stats={hybData.scenarios.D_hybrid_filtered} baseline={hybData.scenarios.A_baseline} />
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Equity Curves Chart */}
          {chartData.length > 0 && (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4" data-testid="equity-chart">
              <h2 className="text-base font-semibold text-zinc-200 mb-3">Equity Curves — {activeSymbol}</h2>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="candle" tick={{ fill: "#71717a", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={v => `${v.toFixed(0)}%`} />
                  <Tooltip
                    contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8 }}
                    labelFormatter={v => `Candle ${v}`}
                    formatter={(v, name) => [`${v?.toFixed(1)}%`, name]}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="baseline" stroke="#22c55e" dot={false} strokeWidth={2} name="Baseline" />
                  <Line type="monotone" dataKey="taker" stroke="#ef4444" dot={false} strokeWidth={1.5} name="Pure Taker" />
                  <Line type="monotone" dataKey="hybrid" stroke="#3b82f6" dot={false} strokeWidth={1.5} name="Hybrid 70/30" />
                  <Line type="monotone" dataKey="filtered" stroke="#f59e0b" dot={false} strokeWidth={2} name="Hybrid+Filters" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Regime Breakdown */}
          {regimeData.length > 0 && (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4" data-testid="regime-chart">
              <h2 className="text-base font-semibold text-zinc-200 mb-3">
                Regime PnL Breakdown — {activeSymbol}
                {symData?.regime_sanity?.regime_concentrated && (
                  <span className="ml-2 text-xs text-amber-400 font-normal">Warning: Concentrated in one regime</span>
                )}
              </h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={regimeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="regime" tick={{ fill: "#71717a", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8 }}
                    formatter={(v, name) => [`$${v?.toFixed(2)}`, name]}
                  />
                  <Bar dataKey="pnl" name="PnL">
                    {regimeData.map((entry, idx) => (
                      <Cell key={idx} fill={REGIME_COLORS[entry.regime] || "#6b7280"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
                {regimeData.map(r => (
                  <div key={r.regime} className="text-center text-xs">
                    <div className="font-mono text-zinc-400">{r.regime}</div>
                    <div className="text-zinc-300">{r.trades}T | {r.win_rate}% WR</div>
                    <div className="text-zinc-500">{r.pnl_contribution_pct}% of PnL</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cross-Symbol Summary */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden" data-testid="cross-symbol-summary">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h2 className="text-base font-semibold text-zinc-200">Cross-Symbol Summary</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-800/40 text-zinc-400 text-xs uppercase">
                    <th className="py-2 px-3 text-left">Symbol</th>
                    <th className="py-2 px-3 text-center">Verdict</th>
                    <th className="py-2 px-3 text-right">Return%</th>
                    <th className="py-2 px-3 text-right">Sharpe</th>
                    <th className="py-2 px-3 text-right">MaxDD%</th>
                    <th className="py-2 px-3 text-right">PF</th>
                    <th className="py-2 px-3 text-right">Degradation</th>
                  </tr>
                </thead>
                <tbody>
                  {SYMBOLS.map(sym => {
                    const hyb = hybridResults[sym];
                    const filtered = hyb?.scenarios?.D_hybrid_filtered;
                    return (
                      <tr key={sym} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="py-2 px-3 font-mono font-medium text-zinc-200">{sym}</td>
                        <td className="py-2 px-3 text-center">
                          {hyb?.verdict ? <VerdictBadge light={hyb.verdict.light} /> : <span className="text-zinc-500 text-xs">pending</span>}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">{filtered?.total_return_pct?.toFixed(1) || "—"}%</td>
                        <td className="py-2 px-3 text-right font-mono">{filtered?.sharpe?.toFixed(2) || "—"}</td>
                        <td className="py-2 px-3 text-right font-mono">{filtered?.max_drawdown_pct?.toFixed(1) || "—"}%</td>
                        <td className="py-2 px-3 text-right font-mono">{filtered?.profit_factor?.toFixed(2) || "—"}</td>
                        <td className="py-2 px-3 text-right font-mono">{hyb?.verdict?.degradation_from_baseline_pct || "—"}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Re-run Button */}
          <div className="flex justify-end">
            <button
              data-testid="rerun-validation-btn"
              onClick={() => launchValidation(activeSymbol)}
              disabled={!!runningTasks[activeSymbol]}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded text-sm font-mono border border-zinc-700 transition-colors disabled:opacity-50"
            >
              {runningTasks[activeSymbol] ? `Running ${runningTasks[activeSymbol].progress.toFixed(0)}%...` : `Re-run on ${activeSymbol}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
